import { dateUtil } from '@Util/date';
import { StatusCard } from '@Page/SalaryTaxCard/component/StatusBar/StatusCard/StatusCard';
import { AltinnData } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { APIError, HTTPError, InlineMarkdown } from '@tlx/astro-shared';

export interface StatusBarTexts {
    heading_unexpected_error: string;
    text_session_valid_until: string;
    text_last_updated_in_tripletex: string;
    message_awaiting_response_from: string;
    text_altinn: string;
    message_taxcards_received: string;
    text_csv_qualifier_none: string;
    message_taxcards_received_singular: string;
    message_taxcards_received_plural: string;
    text_altinn_login_required: string;
    message_taxcards_no_taxcard_received: string;
    message_taxcards_try_again: string;
}

interface StatusProps {
    status: CardVariant;
    altinn: AltinnData;
    texts: StatusBarTexts;
}

export function StatusBar({ status, altinn, texts }: StatusProps) {
    const hyphen = '\u2014'; //Wider hyphen than the minus symbol -
    let children = null;

    switch (status) {
        case CardVariant.Normal:
            if (altinn.isLoggedInUntil || altinn.lastUpdated) {
                children = (
                    <>
                        {altinn.isLoggedInUntil ? (
                            <div>
                                <span style={{ fontWeight: 500 }}>
                                    {texts.text_session_valid_until.replace(':', '')}
                                </span>
                                <span>{` ${hyphen} ${formatDate(altinn.isLoggedInUntil, false)}`}</span>
                            </div>
                        ) : null}
                        {altinn.lastUpdated ? (
                            <div>
                                <span style={{ fontWeight: 500 }}>{texts.text_last_updated_in_tripletex}</span>
                                <span>{` ${hyphen} ${formatDate(altinn.lastUpdated, true)}`}</span>
                            </div>
                        ) : null}
                    </>
                );
            }
            break;
        //KGFIXME spinner_receiving and delivering same, no need to differ between
        case CardVariant.Spinner:
            // eslint-disable-next-line no-case-declarations
            const text = texts.message_awaiting_response_from + ' ' + texts.text_altinn;
            children = <div>{text}</div>;
            break;
        case CardVariant.Success:
            if (altinn.numCardsReceived == 0) {
                const text = texts.message_taxcards_received + ' ' + texts.text_csv_qualifier_none.toLowerCase();
                children = <div>{text}</div>;
            } else {
                children = (
                    <div>
                        {altinn.numCardsReceived == 1
                            ? texts.message_taxcards_received_singular
                            : texts.message_taxcards_received_plural}
                    </div>
                );
            }
            break;
        case CardVariant.Warning_login_altinn:
            children = <div>{texts.text_altinn_login_required}</div>;
            break;
        case CardVariant.Warning_retry_fetch:
            children = (
                <span>{`${texts.message_taxcards_no_taxcard_received} ${texts.message_taxcards_try_again}`}</span>
            );
            break;
        case CardVariant.Error:
            children = <div>{textFromValidations(altinn.error, texts.heading_unexpected_error)}</div>;
            break;
        default:
            return null;
    }

    return <StatusCard cardStyle={getStyle(status)}>{children}</StatusCard>;
}

function textFromValidations(err: Error | undefined, unexpectedErrorHeading: string) {
    // the type here is really bogus - I know it's error when it's called but ! is not allowed by eslint IIRC
    if (err instanceof APIError) {
        if (err.details.validationMessages) {
            if (err.details.validationMessages.length == 1) {
                return <InlineMarkdown>{err.details.validationMessages[0]?.message || ''}</InlineMarkdown>;
            }
            return (
                <ul>
                    {err.details.validationMessages.map((x) => (
                        <li key={x.message}>
                            <InlineMarkdown>{x.message}</InlineMarkdown>
                        </li>
                    ))}
                </ul>
            );
        } else {
            throw err; // produces the nice "an unexpected error has occurred" standard Tripletex page
        }
    } else if (err instanceof HTTPError) {
        throw err;
    }
    return unexpectedErrorHeading;
}

export enum CardVariant {
    Normal,
    Info,
    Success,
    Warning_login_altinn,
    Warning_retry_fetch,
    Error,
    Spinner,
    Unknown,
}

const errorCard = {
    borderColor: 'taxcard-border__red',
    backgroundColor: 'atl-bg-red-5',
    color: 'atl-text-red-100',
    bg: 'atl-bg-red-10',
    icon: 'error_outline',
};

const warningCard = {
    borderColor: 'taxcard-border__yellow',
    backgroundColor: 'atl-bg-yellow-5',
    color: 'atl-text-yellow-100',
    bg: 'atl-bg-yellow-10',
    icon: 'report_problem',
};

const successCard = {
    borderColor: 'taxcard-border__green',
    backgroundColor: 'atl-bg-green-5',
    color: 'atl-text-green-100',
    bg: 'atl-bg-green-10',
    icon: 'check_circle',
};

const infoCard = {
    borderColor: 'taxcard-border__blue',
    backgroundColor: 'atl-bg-blue-5',
    color: 'atl-text-blue-100',
    bg: 'atl-bg-blue-10',
    icon: 'info',
};
const normalCard = {
    ...infoCard,
    icon: 'update',
};

const spinnerCard = {
    borderColor: 'taxcard-border__blue',
    backgroundColor: 'atl-bg-blue-5',
    color: 'atl-text-blue-100',
    bg: 'atl-bg-blue-10',
    icon: CardVariant.Spinner,
};

export function getStyle(variant: CardVariant) {
    switch (variant) {
        case CardVariant.Error:
            return errorCard;
        case CardVariant.Warning_login_altinn:
            return warningCard;
        case CardVariant.Warning_retry_fetch:
            return warningCard;
        //All types of success
        case CardVariant.Success:
            return successCard;
        case CardVariant.Normal:
            return normalCard;
        case CardVariant.Info:
            return infoCard;
        //All types of spinners
        case CardVariant.Spinner:
            return spinnerCard;
        default:
            return undefined;
    }
}

function formatDate(date: Date, includeTime: boolean) {
    //atlas also has a dateUtil.formatDate but doesnt look like that takes in locale
    //dateUtil 'a' adds AM/PM, but if local is norwegian it should add 'kl.' before instead :)
    let formattedDate = '';
    if (includeTime) {
        formattedDate += `${dateUtil.formatDate(date, 'no')}, `;
    }
    formattedDate +=
        window.locale != 'en_GB'
            ? ' kl. ' + dateUtil.formatDate(date, 'no') //24 hour clock
            : dateUtil.formatDate(date, 'en'); //12 hour clock AM/PM
    return formattedDate;
}
